import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription, Observable, map } from 'rxjs';


import { ProjectsFormsService } from '@app/services/app/projects-forms.service';
import { CustomFormSubmission, CustomFormFieldSubmission, FieldFilterAssociation } from '@core/objects/form';
import { FieldSubmissionValueInterface, FieldSubmissionValidity, FieldSubmissionResponseInterface } from '@core/custom-forms/models/submissions/submissions-custom-form-field-data.model';
import * as Objects from '@core/objects';

@Component({
    selector: 'project-details-forms-fill-main',
    templateUrl: './fill-main.component.html',
    styleUrls: ['./fill-main.component.scss']
})
export class ProjectDetailsFormsFillMainComponent implements OnInit, OnDestroy  {
    public DYNAMIC_FIELD_TYPE = 'artwork-link-selector-v1';
    public ASSOCIATED_MULTIPLE_TAG_TYPE = 'multiple-tag-selector-v1';
    public ASSOCIATED_SINGLE_TAG_TYPE = 'single-tag-selector-v1';


    public MODAL_VIEW_NONE = 0;
    public MODAL_VIEW_FIELD_VALIDATION_ERROR = 1;

	public readonly STATUS_LOADING = 0;
	public readonly STATUS_DONE = 1;

    @Input() customForm: CustomFormSubmission;
    @Output() collapsible: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() cancel = new EventEmitter<boolean>(); // boolean not used
	
    fields$: Observable<CustomFormFieldSubmission[]>;
    loadingFields$: Observable<boolean>;
    errorFields$: Observable<HttpErrorResponse>;
    errorForm$: Observable<HttpErrorResponse>;
    // submissionValues$: Observable<FieldSubmissionValueInterface[]>;

    public modalView: number = this.MODAL_VIEW_NONE;
    // Management for aw link fileds and their dynamic field 
    // an array to hold the dynamic fields tag id values for each field IDs
    public dynamicFieldAssociations: { [uuid: string]: FieldFilterAssociation[] } = {};
    public buttonDisabled: boolean = true; // on open there will be no need to save.
	// public selectedEvent: any = '';
	public saveBtntext: string = 'Save';
    public allFieldsReady: boolean = false;
    public form: FormGroup;

    public fieldsWithErrors: { [alias: string]: FieldSubmissionResponseInterface } = {}; // Store errors by field alias
    private fieldValidities: { [key: string]: boolean } = {};
	private subs: Subscription[] = [];

	constructor(private projectFormsSrv: ProjectsFormsService,
        private cdr: ChangeDetectorRef,
        private formBuilder: FormBuilder){}


	ngOnInit(){
        this.form = this.formBuilder.group({
            name: ['', [Validators.required]]
		});
        
        // Initialize the observable for store data
        // this.submissionValues$ = this.projectFormsSrv.selectFieldValues(this.customForm.uuid);
        this.fields$ = this.projectFormsSrv.selectFormFields(this.customForm.uuid);
        this.loadingFields$ = this.projectFormsSrv.selectLoadingFields(this.customForm.uuid);
        this.errorFields$ = this.projectFormsSrv.selectErrorFields(this.customForm.uuid);
        this.errorForm$ = this.projectFormsSrv.selectErrorForms();
        this.projectFormsSrv.loadFormFieldSubmissions(this.customForm.uuid);

        this.subs.push(
            this.fields$.subscribe((fieldSubmissions: CustomFormFieldSubmission[]) => {
                this.updateDynamicFieldAssociations(fieldSubmissions);
            })
        
        );

        this.subs.push(
            this.errorFields$.subscribe((errorResponse: HttpErrorResponse) => {
                if(!errorResponse){
                    this.modalView = this.MODAL_VIEW_NONE;
                    return;
                }
                // check the list of fields failed
                // watch out the interface FieldSubmissionResponseInterface 
                if (errorResponse && errorResponse.status === 406 && Array.isArray(errorResponse.error.errors)) {

                    // Extract field-level errors and store them in `fieldsWithErrors`
                    this.fieldsWithErrors = errorResponse.error.errors.reduce((acc, fieldError) => {
                        acc[fieldError.alias] = fieldError;
                        return acc ;
                    }, {});

                    this.modalView = this.MODAL_VIEW_FIELD_VALIDATION_ERROR;
                }
            })
        
        ); // end push subscriber

    }

    ngOnDestroy() {
        this.collapsible.emit(true);
        this.subs.forEach((sub: Subscription) => sub.unsubscribe());
    }

	onCancel() {
        this.cleanForm();
        this.cancel.emit(true);
	}

    onSubmit(){
        // this.saveBtntext = "Saving...";
        this.buttonDisabled = true;
        this.projectFormsSrv.submitFieldValues(this.customForm.uuid);
        this.collapsible.emit(true);
    }


    cleanForm(){
        this.projectFormsSrv.cleanFormErrors();
        this.projectFormsSrv.cleanFormFieldValues();
    }

    onError(): void {
        this.projectFormsSrv.cleanFormErrors();
    }

    // Check if a field has an error
    hasFieldError(alias: string): boolean {
        return this.fieldsWithErrors.hasOwnProperty(alias);
    }

    // Get error message for a field
    getFieldError(alias: string): FieldSubmissionResponseInterface | {} {
        return this.fieldsWithErrors[alias] || {};
    }

    onErrorFields(){
        this.modalView = this.MODAL_VIEW_NONE;
    }

    onFieldValueChange(fieldValue: FieldSubmissionValueInterface){
        // call the service to update in the store:
        this.projectFormsSrv.setFieldValue(this.customForm.uuid,fieldValue);
        this.buttonDisabled = false;
        // Check if the fieldValue.uuid is in some assocciated 
        for (const fieldLinkUuid in this.dynamicFieldAssociations) {
            // Check if the dynamic field UUIDs contain the fieldValue.uuid
            if (this.dynamicFieldAssociations[fieldLinkUuid].some((fsa:FieldFilterAssociation) => fsa.uuid === fieldValue.uuid)) {
                // Find or create the association for this field link
                const association = this.getFieldAssociation(fieldLinkUuid, fieldValue.uuid);
                // Update the values based on the type
                const newValues = this.extractValueTagIds(fieldValue.value);
                //  console.log(`Updated dynamic field values for field-link UUID`,fieldValue, newValues)
                // this.updateAssociationValues(association, newValues);
                this.dynamicFieldAssociations[fieldLinkUuid] = this.dynamicFieldAssociations[fieldLinkUuid]
                    .map((fsa:FieldFilterAssociation) => (fsa.uuid === fieldValue.uuid) ? {...fsa, values: newValues } : fsa );
                // console.log(`Updated dynamic field values for field-link UUID: ${fieldValue.uuid}`, this.dynamicFieldAssociations[fieldLinkUuid]);
                // }
                break; // Exit loop if found
            }
        }
        // emit collapsible false to avoid close form when any field has changes.
        this.collapsible.emit(false);
        // Clear error for this specific field
        if (this.fieldsWithErrors[fieldValue.alias]) {
            delete this.fieldsWithErrors[fieldValue.alias];
        }
  
    }

    onFieldValidityChange(validity: FieldSubmissionValidity){

        this.fieldValidities[validity.id] = validity.valid;
        this.checkAllFieldsValid();
    }

    private updateDynamicFieldAssociations(fieldSubmissions: CustomFormFieldSubmission[]) : void {
        // Step 1: Reset the associations for each subscription
        this.dynamicFieldAssociations = {};
        // Step 2: Filter for dynamic fields and populate the associative array
        fieldSubmissions
            .filter((fs1:CustomFormFieldSubmission) => fs1.field.type === this.DYNAMIC_FIELD_TYPE)
            .forEach((fs2:CustomFormFieldSubmission) => {
                const dynamicFieldValues = fs2.field.options.dynamic_field; // Assuming this is an array<uuid>
                // Initialize the dynamicFieldAssociations for the field uuid if not present
                if (!this.dynamicFieldAssociations[fs2.field.uuid]) {
                    this.dynamicFieldAssociations[fs2.field.uuid] = [];
                }

                // Step 4: Check if dynamicFieldValues is an array or a single object
                fieldSubmissions.filter((fs3:CustomFormFieldSubmission) => dynamicFieldValues.some((fuuid: string) => fs3.field.uuid === fuuid))
                    .forEach((fieldAssoc:CustomFormFieldSubmission) => {

                        
                        this.dynamicFieldAssociations[fs2.field.uuid].push(this.extractFieldTagIds(fieldAssoc));
                    });
            });

        // console.log('Dynamic Field Associations:', this.dynamicFieldAssociations);
    }

    private checkAllFieldsValid(): void {
        // avoid ExpressionChangedAfterItHasBeenCheckedError -> https://v17.angular.io/errors/NG0100
        Promise.resolve().then(() => this.allFieldsReady = Object.values(this.fieldValidities).every(valid => valid) );
    }

    // 
    /**
     * 
     * @param value value of the associated fieldsubmission could be several types in the future
     * @returns the resulting FieldFilterAssociation with the uuid, key  and the value
     * handles the extraction of IDs 
     */
    private extractFieldTagIds(associatedField: CustomFormFieldSubmission): FieldFilterAssociation {
        const tagFilterValues: number[] = [];
        let filterkey:string = 'label';
        // TODO: for the future check field type here instead of value type.
        switch (associatedField.field.type) {
            case this.ASSOCIATED_MULTIPLE_TAG_TYPE:
                // If it's an array (multiple-tag-selector-v1), extract ids from all TAG objects
                const ids = associatedField.serialized_value ? associatedField.serialized_value.map((tag: Objects.Label) => tag.id) : [];
                tagFilterValues.push(...ids);
                filterkey = 'label';
                break;
            case this.ASSOCIATED_SINGLE_TAG_TYPE:
                // If it's a single TAG object (simgle-tag-selector-v1), extract the id
                if(associatedField.serialized_value?.id)
                    tagFilterValues.push(associatedField.serialized_value.id );
                filterkey = 'label';
                break;
            default:
                // tagFilterValues.push(null);
                filterkey = '';
                break;
        }
        const association: FieldFilterAssociation = {
            key: filterkey, // always
            uuid: associatedField.field.uuid, // set the uuid asscoited
            values: tagFilterValues
        };

        return association;
    }

    // Helper method to get or create the FieldAssociation for a given fieldLinkUuid and fieldUuid
    private getFieldAssociation(fieldLinkUuid: string, assocUuid: string): FieldFilterAssociation {
        let association: FieldFilterAssociation = this.dynamicFieldAssociations[fieldLinkUuid].find((fsa:FieldFilterAssociation) => fsa.uuid === assocUuid);
        if (!association) {
            // Create a new association if it doesn't exist label for now
            association = { key: 'label', uuid: assocUuid, values: [] };
            this.dynamicFieldAssociations[fieldLinkUuid] = this.dynamicFieldAssociations[fieldLinkUuid] || [];
            this.dynamicFieldAssociations[fieldLinkUuid].push(association);
        }
        return association;
    }

    // Function to extract new tag IDs from the value
    private extractValueTagIds(value: any): number[] {
        if (Array.isArray(value)) {
            return value;
        } else if (value) {
            return [value]; // Single id case
        }
        return []; // Default case, no IDs found
    }

}
